import React, { useState, useEffect, useMemo } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithCustomToken,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  ShoppingCart,
  Plus,
  Trash2,
  Edit,
  X,
  Search,
  Smartphone,
  Watch,
  Tablet,
  Headphones,
  User,
  Lock,
  LayoutGrid,
  LogOut,
  Check,
  MessageCircle,
  MapPin,
  Phone,
  User as UserIcon,
  Shield,
  ChevronDown,
  Loader,
  Mail,
  Star,
  Image as ImageIcon,
  Upload,
  Zap,
} from "lucide-react";

// --- CONFIGURATION ---
const SHOP_PHONE_NUMBER = "212600000000"; // Ton numéro WhatsApp
const ADMIN_EMAIL = "ziadbenali4@gmail.com"; // Ton email admin

// --- Configuration Firebase ---
// Pour la production, assure-toi que ta variable d'environnement VITE_FIREBASE_CONFIG est bien définie
// Sinon, remplace l'objet de fallback ci-dessous par tes vraies clés si tu ne gères pas encore les variables d'env.
const firebaseConfig = {
  apiKey: "AIzaSyATPoA6If9qZYa0oKKH2sxVbPS0nBAT0u4",
  authDomain: "techzone-app-50eb2.firebaseapp.com",
  projectId: "techzone-app-50eb2",
  storageBucket: "techzone-app-50eb2.firebasestorage.app",
  messagingSenderId: "353158791878",
  appId: "1:353158791878:web:66f6fe059518901f054dc7",
  measurementId: "G-WMMFXVZ9LX"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// ID de l'application pour la structure de données
const appId = "techzone-public";

// --- Composants UI Utilitaires ---

const Button = ({
  children,
  onClick,
  variant = "primary",
  className = "",
  disabled,
  type = "button",
  ...props
}) => {
  const baseStyle =
    "px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100";
  const variants = {
    primary:
      "bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg",
    secondary:
      "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-100",
    ghost: "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
    whatsapp: "bg-green-500 text-white hover:bg-green-600 shadow-md",
    google:
      "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 text-sm w-full shadow-sm",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      type={type}
      className={`${baseStyle} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({ label, icon: Icon, subLabel, ...props }) => (
  <div className="flex flex-col gap-1 mb-3">
    <div className="flex justify-between items-baseline">
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}
      {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
    </div>
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <Icon size={18} />
        </div>
      )}
      <input
        className={`w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
          Icon ? "pl-10 pr-3" : "px-3"
        }`}
        {...props}
      />
    </div>
  </div>
);

const formatPrice = (price) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "MAD", // J'ai changé en MAD vu que tu es au Maroc (212)
  }).format(price);
};

const calculateDiscount = (original, promo) => {
  if (!promo || promo >= original) return 0;
  return Math.round(((original - promo) / original) * 100);
};

// --- Composant Principal ---

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // États de l'interface
  const [view, setView] = useState("shop");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // États Auth
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // États Admin Editing
  const [editingProduct, setEditingProduct] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  // États Checkout
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  // États Filtres
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [searchQuery, setSearchQuery] = useState("");

  // --- Initialisation Auth ---
  useEffect(() => {
    const initAuth = async () => {
      if (!auth.currentUser) {
        await signInAnonymously(auth).catch((err) =>
          console.error("Erreur Auth Anonyme:", err)
        );
      }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      // Vérification Admin sécurisée côté client (doit être renforcée par les règles Firestore)
      if (u && !u.isAnonymous && u.email === ADMIN_EMAIL) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- Chargement des Données ---
  useEffect(() => {
    // Lecture publique pour les produits
    const productsRef = collection(
      db,
      "artifacts",
      appId,
      "public",
      "data",
      "products"
    );

    const unsubProducts = onSnapshot(
      productsRef,
      (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        items.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );
        setProducts(items);
      },
      (error) => console.error("Erreur produits:", error)
    );

    // Lecture des commandes (uniquement si admin ou pour ses propres commandes - simplifié ici)
    let unsubOrders = () => {};
    if (isAdmin) {
      const ordersRef = collection(
        db,
        "artifacts",
        appId,
        "public",
        "data",
        "orders"
      );
      unsubOrders = onSnapshot(
        ordersRef,
        (snapshot) => {
          const items = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          items.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
          setOrders(items);
        },
        (error) => console.error("Erreur commandes:", error)
      );
    }

    return () => {
      unsubProducts();
      unsubOrders();
    };
  }, [user, isAdmin]);

  // --- Helpers Image Upload ---
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 800000) {
        alert(
          "L'image est trop volumineuse (Max 800kb). Utilisez un lien externe ou une image compressée."
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const setDemoImage = (type) => {
    const map = {
      phone:
        "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=500&q=80",
      watch:
        "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&q=80",
      tablet:
        "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=500&q=80",
      audio:
        "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500&q=80",
    };
    setImagePreview(map[type]);
  };

  // --- Auth Handlers ---
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      if (authMode === "login") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
      setIsLoginModalOpen(false);
    } catch (error) {
      console.error(error);
      alert("Erreur d'authentification. Vérifiez vos identifiants.");
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      setIsLoginModalOpen(false);
      if (result.user.email === ADMIN_EMAIL) setView("admin-dashboard");
    } catch (error) {
      console.error(error);
      alert("Erreur Google. Utilisez l'email si le popup est bloqué.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setIsAdmin(false);
    setView("shop");
    setUserMenuOpen(false);
    await signInAnonymously(auth);
  };

  // --- Cart & Checkout ---
  const addToCart = (product) => {
    if (product.stock <= 0) return;
    setCart((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) {
          alert("Stock maximum atteint pour cet article !");
          return prev;
        }
        return prev.map((p) =>
          p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id) =>
    setCart((prev) => prev.filter((p) => p.id !== id));

  const updateQuantity = (id, delta) =>
    setCart((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, quantity: Math.max(1, p.quantity + delta) } : p
      )
    );

  const cartTotal = cart.reduce((acc, item) => {
    const finalPrice =
      item.promoPrice && item.promoPrice < item.price
        ? item.promoPrice
        : item.price;
    return acc + finalPrice * item.quantity;
  }, 0);

  const handleStartCheckout = () => {
    if (!user || user.isAnonymous) {
      setIsCartOpen(false);
      setIsLoginModalOpen(true);
      return;
    }
    setIsCheckoutOpen(true);
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!user || user.isAnonymous) return;

    const formData = new FormData(e.target);
    const customerData = {
      name: formData.get("name"),
      phone: formData.get("phone"),
      address: formData.get("address"),
      email: user.email || "Non spécifié",
      userId: user.uid,
    };

    try {
      // Mise à jour du stock
      cart.forEach(async (item) => {
        const productRef = doc(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "products",
          item.id
        );
        // Note : Idéalement, faire cela via une transaction ou Cloud Function
        const newStock = Math.max(0, item.stock - item.quantity);
        await updateDoc(productRef, { stock: newStock });
      });

      // Création de la commande
      await addDoc(
        collection(db, "artifacts", appId, "public", "data", "orders"),
        {
          customer: customerData,
          items: cart,
          total: cartTotal,
          status: "En attente",
          date: serverTimestamp(),
        }
      );

      let message = `👋 Nouvelle commande TechStore !\n\n`;
      cart.forEach((item) => {
        const p =
          item.promoPrice && item.promoPrice < item.price
            ? item.promoPrice
            : item.price;
        message += `- ${item.name} (x${item.quantity}) : ${formatPrice(
          p * item.quantity
        )}\n`;
      });
      message += `\n💰 Total: ${formatPrice(cartTotal)}\n\n`;
      message += `📍 ${customerData.name}\n📞 ${customerData.phone}\n🏠 ${customerData.address}`;

      const whatsappUrl = `https://wa.me/${SHOP_PHONE_NUMBER}?text=${encodeURIComponent(
        message
      )}`;
      setCart([]);
      setIsCheckoutOpen(false);
      setIsCartOpen(false);
      window.open(whatsappUrl, "_blank");
    } catch (err) {
      console.error("Erreur commande:", err);
      alert("Une erreur est survenue lors de la commande.");
    }
  };

  // --- Admin Logic ---
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    const formData = new FormData(e.target);
    const price = parseFloat(formData.get("price"));
    const promoPrice = formData.get("promoPrice")
      ? parseFloat(formData.get("promoPrice"))
      : 0;

    const productData = {
      name: formData.get("name"),
      price: price,
      promoPrice: promoPrice,
      category: formData.get("category"),
      image: imagePreview || "https://via.placeholder.com/300",
      description: formData.get("description"),
      isFeatured: formData.get("isFeatured") === "on",
      stock: parseInt(formData.get("stock")) || 0,
      updatedAt: serverTimestamp(),
    };
    try {
      const collectionRef = collection(
        db,
        "artifacts",
        appId,
        "public",
        "data",
        "products"
      );

      if (editingProduct) {
        await updateDoc(doc(collectionRef, editingProduct.id), productData);
      } else {
        await addDoc(collectionRef, {
          ...productData,
          createdAt: serverTimestamp(),
        });
      }
      setIsProductModalOpen(false);
      setEditingProduct(null);
      setImagePreview("");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la sauvegarde (Vérifiez vos droits admin).");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!isAdmin) return;
    if (confirm("Êtes-vous sûr ?"))
      await deleteDoc(
        doc(db, "artifacts", appId, "public", "data", "products", id)
      );
  };

  // --- Data & Filtering ---
  const categories = [
    { id: "Tous", icon: LayoutGrid },
    { id: "Téléphones", icon: Smartphone },
    { id: "Tablettes", icon: Tablet },
    { id: "Montres", icon: Watch },
    { id: "Accessoires", icon: Headphones },
  ];

  const featuredProducts = products.filter((p) => p.isFeatured);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      return (
        (activeCategory === "Tous" || p.category === activeCategory) &&
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [products, activeCategory, searchQuery]);

  const isNew = (date) => {
    if (!date) return false;
    const now = new Date();
    const prodDate = date.toDate();
    const diffTime = Math.abs(now - prodDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7; // Augmenté à 7 jours pour "Nouveau"
  };

  if (authLoading)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader className="animate-spin text-blue-600" />
      </div>
    );

  // --- VUE DASHBOARD ADMIN ---
  if (view === "admin-dashboard" && isAdmin) {
    // ... [Le code de la vue admin reste identique au tiens, j'ai juste vérifié qu'il n'y a pas d'erreur]
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row justify-between md:items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="text-blue-600" /> Admin Panel
            </h1>
            <div className="flex gap-2 overflow-x-auto">
              <Button onClick={() => setView("orders")} variant="secondary">
                <Check size={18} /> Commandes
              </Button>
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setImagePreview("");
                  setIsProductModalOpen(true);
                }}
                variant="primary"
              >
                <Plus size={18} /> Ajouter
              </Button>
              <Button variant="ghost" onClick={() => setView("shop")}>
                <LogOut size={18} /> Site
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 py-8">
          {view === "orders" ? (
            <div className="space-y-4">
              {orders.length === 0 ? (
                <p className="text-gray-500">Aucune commande.</p>
              ) : (
                orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white p-4 rounded-xl shadow-sm border flex justify-between items-center"
                  >
                    <div>
                      <p className="font-bold">
                        {order.customer.name}{" "}
                        <span className="text-gray-400 font-normal text-sm">
                          | {order.customer.phone}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500">
                        {order.items.length} articles
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="block font-bold text-green-600">
                        {formatPrice(order.total)}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(
                          order.date?.seconds * 1000
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <Button
                variant="secondary"
                onClick={() => setView("admin-dashboard")}
              >
                Retour
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
              <table className="w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                  <tr>
                    <th className="px-6 py-4">Produit</th>
                    <th className="px-6 py-4">Stock</th>
                    <th className="px-6 py-4">Prix</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 flex items-center gap-3">
                        <div className="relative">
                          <img
                            src={p.image}
                            className="w-10 h-10 rounded bg-gray-100 object-cover"
                            alt={p.name}
                          />
                          {p.isFeatured && (
                            <div className="absolute -top-1 -right-1 bg-yellow-400 text-white rounded-full p-0.5">
                              <Star size={8} fill="currentColor" />
                            </div>
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 block">
                            {p.name}
                          </span>
                          {p.promoPrice > 0 && (
                            <span className="text-xs text-red-500 font-bold">
                              Promo
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            p.stock < 5
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {p.stock}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-mono">
                        {formatPrice(p.price)}
                      </td>
                      <td className="px-6 py-4 text-right flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingProduct(p);
                            setImagePreview(p.image);
                            setIsProductModalOpen(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(p.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>

        {/* MODAL PRODUIT (Copie conforme de ton code, juste indentation) */}
        {isProductModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">
                {editingProduct ? "Modifier" : "Ajouter"}
              </h3>
              <form onSubmit={handleSaveProduct} className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-300 text-center">
                  {imagePreview ? (
                    <div className="relative w-32 h-32 mx-auto">
                      <img
                        src={imagePreview}
                        className="w-full h-full object-cover rounded-lg shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setImagePreview("")}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center text-blue-500">
                        <ImageIcon size={32} />
                      </div>
                      <p className="text-sm text-gray-500">
                        Glissez une image ou importez
                      </p>
                      <div className="relative">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="mx-auto text-xs"
                        >
                          <Upload size={14} /> Importer un fichier
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </div>
                      <div className="flex justify-center gap-2 pt-2">
                        {/* Boutons démo conservés */}
                        <button
                          type="button"
                          onClick={() => setDemoImage("phone")}
                          className="text-xs text-blue-600 underline"
                        >
                          Tel
                        </button>
                        <button
                          type="button"
                          onClick={() => setDemoImage("watch")}
                          className="text-xs text-blue-600 underline"
                        >
                          Montre
                        </button>
                        <button
                          type="button"
                          onClick={() => setDemoImage("tablet")}
                          className="text-xs text-blue-600 underline"
                        >
                          Tab
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <Input
                  name="name"
                  label="Nom"
                  defaultValue={editingProduct?.name}
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    name="stock"
                    label="Stock"
                    type="number"
                    defaultValue={editingProduct?.stock || 10}
                    required
                    subLabel="Qté dispo"
                  />
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Catégorie
                    </label>
                    <select
                      name="category"
                      className="px-3 py-2 border rounded-lg h-[42px]"
                      defaultValue={editingProduct?.category}
                    >
                      {categories
                        .filter((c) => c.id !== "Tous")
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.id}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                  <Input
                    name="price"
                    label="Prix Standard"
                    type="number"
                    step="0.01"
                    defaultValue={editingProduct?.price}
                    required
                  />
                  <Input
                    name="promoPrice"
                    label="Prix Promo"
                    type="number"
                    step="0.01"
                    defaultValue={editingProduct?.promoPrice}
                    subLabel="Optionnel"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    name="isFeatured"
                    defaultChecked={editingProduct?.isFeatured}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <label
                    htmlFor="isFeatured"
                    className="font-medium text-gray-700 flex items-center gap-1"
                  >
                    <Star
                      size={16}
                      className="text-yellow-500"
                      fill="currentColor"
                    />{" "}
                    Mettre en avant sur l'accueil
                  </label>
                </div>

                <textarea
                  name="description"
                  className="w-full border rounded-lg p-2 text-sm"
                  rows="3"
                  defaultValue={editingProduct?.description}
                  placeholder="Description technique..."
                ></textarea>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setIsProductModalOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" className="flex-1">
                    Sauvegarder
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- VUE CLIENT (SHOP) ---
  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-20">
      <nav className="bg-white shadow-sm sticky top-0 z-40 bg-white/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              setActiveCategory("Tous");
              setSearchQuery("");
              setView("shop"); // Assure de revenir au shop si on clique sur le logo
            }}
          >
            <div className="bg-blue-600 p-1.5 rounded-lg">
              <Smartphone className="text-white w-6 h-6" />
            </div>
            <span className="text-xl font-bold text-blue-900 hidden sm:block">
              TechZone
            </span>
          </div>
          <div className="flex-1 max-w-md mx-4 bg-gray-100 rounded-full flex items-center px-4 py-1.5">
            <Search className="text-gray-400 w-4 h-4 mr-2" />
            <input
              type="text"
              placeholder="Rechercher..."
              className="bg-transparent border-none w-full text-sm focus:outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() =>
                  user && !user.isAnonymous
                    ? setUserMenuOpen(!userMenuOpen)
                    : setIsLoginModalOpen(true)
                }
                className={`p-1 flex items-center gap-2 rounded-full transition-all ${
                  user && !user.isAnonymous
                    ? "bg-blue-50 pr-3 border border-blue-100"
                    : "text-gray-400 hover:text-blue-600"
                }`}
              >
                {user && !user.isAnonymous ? (
                  <>
                    <img
                      src={
                        user.photoURL ||
                        `https://ui-avatars.com/api/?name=${user.email}`
                      }
                      className="w-8 h-8 rounded-full border border-white shadow-sm"
                    />
                    <ChevronDown size={14} className="text-blue-600" />
                  </>
                ) : (
                  <User size={24} />
                )}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 animate-in fade-in slide-in-from-top-2">
                  <div className="px-4 py-2 border-b border-gray-50">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {user.displayName || "Client"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user.email}
                    </p>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setView("admin-dashboard");
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Shield size={16} /> Admin Panel
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut size={16} /> Déconnexion
                  </button>
                </div>
              )}
            </div>
            <button
              className="relative p-2 text-blue-600 bg-blue-50 rounded-full"
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingCart size={22} />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                  {cart.reduce((a, b) => a + b.quantity, 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Section Produits Phares (Featured) */}
        {featuredProducts.length > 0 &&
          activeCategory === "Tous" &&
          !searchQuery && (
            <div className="mb-10">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Zap className="text-yellow-500" fill="currentColor" /> Les
                Incontournables
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {featuredProducts.map((p) => (
                  <div
                    key={p.id}
                    className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white flex items-center justify-between relative overflow-hidden group cursor-pointer hover:shadow-xl transition-all"
                    onClick={() => addToCart(p)}
                  >
                    <div className="relative z-10 max-w-[60%]">
                      {p.promoPrice > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                          PROMO -{calculateDiscount(p.price, p.promoPrice)}%
                        </span>
                      )}
                      <h3 className="text-2xl font-bold mb-1">{p.name}</h3>
                      <p className="text-blue-200 text-sm mb-4 line-clamp-2">
                        {p.description}
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold">
                          {formatPrice(p.promoPrice || p.price)}
                        </span>
                        {p.promoPrice > 0 && (
                          <span className="text-sm text-blue-300 line-through">
                            {formatPrice(p.price)}
                          </span>
                        )}
                      </div>
                    </div>
                    <img
                      src={p.image}
                      className="absolute right-0 top-1/2 -translate-y-1/2 h-48 w-48 object-cover rounded-l-full group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Categories */}
        <div className="flex overflow-x-auto gap-3 pb-4 no-scrollbar mb-4">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-white text-gray-600 border"
              }`}
            >
              <cat.icon size={16} /> {cat.id}
            </button>
          ))}
        </div>

        {/* Grille Produits Standard */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {filteredProducts.map((p) => {
            const hasDiscount = p.promoPrice && p.promoPrice < p.price;
            const isOutOfStock = p.stock <= 0;
            return (
              <div
                key={p.id}
                className={`bg-white rounded-xl border hover:shadow-lg transition-all flex flex-col overflow-hidden group relative ${
                  isOutOfStock ? "opacity-70" : ""
                }`}
              >
                {isNew(p.createdAt) && !isOutOfStock && (
                  <span className="absolute top-2 left-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded z-10">
                    NOUVEAU
                  </span>
                )}
                {hasDiscount && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded z-10">
                    -{calculateDiscount(p.price, p.promoPrice)}%
                  </span>
                )}

                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  <img
                    src={p.image}
                    className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  />
                  {!isOutOfStock && (
                    <button
                      onClick={() => addToCart(p)}
                      className="absolute bottom-3 right-3 bg-white p-2 rounded-full shadow-md text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  )}
                  {isOutOfStock && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold uppercase tracking-widest text-sm backdrop-blur-[2px]">
                      Rupture
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="text-xs text-blue-600 font-semibold mb-1">
                    {p.category}
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2">
                    {p.name}
                  </h3>
                  <div className="mt-auto">
                    {hasDiscount ? (
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 line-through">
                          {formatPrice(p.price)}
                        </span>
                        <span className="font-bold text-lg text-red-600">
                          {formatPrice(p.promoPrice)}
                        </span>
                      </div>
                    ) : (
                      <div className="font-bold text-lg">
                        {formatPrice(p.price)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Floating Help Button */}
      <button
        onClick={() =>
          window.open(`https://wa.me/${SHOP_PHONE_NUMBER}`, "_blank")
        }
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-105 transition-all z-30 flex items-center gap-2"
      >
        <MessageCircle size={24} />
        <span className="hidden md:inline font-bold">Aide</span>
      </button>

      {/* Modal de Connexion */}
      {isLoginModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 relative">
            <button
              onClick={() => setIsLoginModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <User size={28} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {authMode === "login" ? "Connexion" : "Inscription"}
              </h2>
            </div>
            <div className="space-y-4">
              <Button variant="google" onClick={handleGoogleLogin}>
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"
                  />
                </svg>
                Continuer avec Google
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Ou avec Email
                  </span>
                </div>
              </div>
              <form onSubmit={handleEmailAuth} className="space-y-3">
                <Input
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="exemple@mail.com"
                  icon={Mail}
                  required
                />
                <Input
                  name="password"
                  label="Mot de passe"
                  type="password"
                  placeholder="••••••••"
                  icon={Lock}
                  required
                />
                <Button type="submit" className="w-full">
                  {authMode === "login" ? "Se connecter" : "S'inscrire"}
                </Button>
              </form>
              <div className="text-center pt-2">
                <button
                  onClick={() =>
                    setAuthMode(authMode === "login" ? "register" : "login")
                  }
                  className="text-sm text-blue-600 hover:underline font-medium"
                >
                  {authMode === "login"
                    ? "Pas de compte ? S'inscrire"
                    : "Déjà un compte ? Se connecter"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart & Checkout */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsCartOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full animate-slideInRight">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <ShoppingCart /> Panier
              </h2>
              <button onClick={() => setIsCartOpen(false)}>
                <X className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  Panier vide
                </div>
              ) : (
                cart.map((item) => {
                  const price =
                    item.promoPrice && item.promoPrice < item.price
                      ? item.promoPrice
                      : item.price;
                  return (
                    <div
                      key={item.id}
                      className="flex gap-3 bg-white border rounded-lg p-2"
                    >
                      <img
                        src={item.image}
                        className="w-16 h-16 rounded object-cover bg-gray-100"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <h4 className="font-semibold text-sm line-clamp-1">
                            {item.name}
                          </h4>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-400"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="flex justify-between items-end mt-2">
                          <div className="flex items-center gap-2 bg-gray-100 rounded px-2">
                            <button onClick={() => updateQuantity(item.id, -1)}>
                              -
                            </button>
                            <span className="text-sm font-bold w-4 text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              disabled={item.quantity >= item.stock}
                            >
                              +
                            </button>
                          </div>
                          <span className="font-bold text-blue-600">
                            {formatPrice(price * item.quantity)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-4 border-t bg-gray-50">
              <div className="flex justify-between text-lg font-bold mb-4">
                <span>Total</span>
                <span>{formatPrice(cartTotal)}</span>
              </div>
              <Button
                className="w-full py-3"
                onClick={handleStartCheckout}
                disabled={cart.length === 0}
              >
                {user && !user.isAnonymous
                  ? "Commander maintenant"
                  : "Se connecter pour commander"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isCheckoutOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold flex gap-2">
                <Check className="text-green-500" /> Validation
              </h3>
              <button onClick={() => setIsCheckoutOpen(false)}>
                <X />
              </button>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
              <p className="font-medium text-blue-900">
                Connecté en tant que : {user?.displayName || user?.email}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Total :{" "}
                <span className="font-bold">{formatPrice(cartTotal)}</span>
              </p>
            </div>
            <form onSubmit={handleCheckoutSubmit} className="space-y-4">
              <Input
                name="name"
                label="Nom"
                defaultValue={user?.displayName}
                icon={UserIcon}
                required
              />
              <Input
                name="phone"
                label="Téléphone"
                type="tel"
                icon={Phone}
                required
              />
              <Input name="address" label="Adresse" icon={MapPin} required />
              <Button
                type="submit"
                variant="whatsapp"
                className="w-full mt-4 py-3"
              >
                <MessageCircle className="mr-2" /> Envoyer (WhatsApp)
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
